/**
 * 埋点管理
 */

const REQUEST_INSTANCE = '__requestInstance__'; // request 初始化后会使用的全局变量名

/**
 * 曝光埋点管理类
 * 负责收集、管理和发送用户行为埋点数据
 */
export class SLExposure {
    /**
     * 构造函数
     * @param {Object} options - 初始化参数
     * @param {Object} options.requestInstance - 请求实例，用于发送埋点数据
     * @param {Array} options.queue - 初始埋点队列，默认为空数组
     * @param {Boolean} options.listenRequestInstance - 是否监听请求实例，默认不监听
     */
    constructor(options = {}) {
        const { 
            queue = [], 
            requestInstance = window?.[REQUEST_INSTANCE] || null,
            listenRequestInstance,
        } = options;
        this.queue = queue;
        this.requestInstance = requestInstance;
        
        // 全局变量监听
        if (!this.requestInstance && listenRequestInstance) {
            Object.defineProperty(window, REQUEST_INSTANCE, {
                set: (value) => {
                    if (!value) return;

                    this.setRequestInstance(value);
                    Object.defineProperty(window, REQUEST_INSTANCE, {
                        value: value,
                        configurable: true,
                        writable: true
                    })
                },
                configurable: true,
            })
        }
    }

    /**
     * 设置请求实例
     * @param {Object} requestInstance - 请求实例
     */
    setRequestInstance(requestInstance) {
        this.requestInstance = requestInstance;
        if (this.queue?.length > 0) {
            this.sendExposureQueue(this.requestInstance);
        }
    }

    /**
     * 构建曝光埋点数据
     * @param {string} trackCode - 埋点代码
     * @param {Object} itemParams - 覆盖埋点参数
     * @returns {Object} 曝光埋点数据
     */
    buildExposureItem(trackCode, itemParams = {}) {
        try {
            const pageName = window?.wa?.PageName || 'None';
            const searchParams = window.URLSearchParams ? new URLSearchParams(window.location.search) : null;
            const cid = searchParams?.get("cid");
            const wtmcid = searchParams?.get("WT.mc_id");

            return {
                "itemCode": trackCode,
                "itemType": 'click',
                "itemPage": pageName,
                "itemScene": 'Consent Mode',
                "itemIndex": 0,
                "itemTime": Date.now(),
                "trackingExt": {
                    cid,
                    wtmcid,
                },
                "reqExt": {
                    ref: window.location.href
                },
                ...itemParams
            };
        } catch (e) {
            console.error('构建埋点数据失败', e);
            return null;
        }
    };

    /**
     * 发送曝光埋点队列
     * @param {Object} requestInstance - 请求实例
     * @returns {Promise} 发送结果
     */
    async sendExposureQueue(requestInstance) {
        if (!requestInstance || !requestInstance.post) {
            return Promise.reject('requestInstance 未初始化');
        }

        // 检查队列是否为空
        if (this.queue.length === 0) {
            return Promise.resolve('队列为空，无需发送');
        }

        try {
            // 复制队列并清空原队列（避免发送过程中新增的埋点丢失）
            const itemsToSend = [...this.queue];
            this.queue = [];

            // 发送埋点数据
            return await requestInstance.post({
                url: '/v1/mobile',
                data: {
                    service: "exposureService.saveExposure(query)",
                    query: {
                        itemList: itemsToSend,
                    },
                }
            });
        } catch (error) {
            console.error('曝光埋点接口请求失败', error);
            return Promise.reject(error);
        }
    };

    /**
     * 添加曝光埋点
     * @param {string} trackCode - 埋点代码
     * @param {Object} options - 埋点参数
     */
    addExposure(trackCode, options = {}) {
        // 剔除 pay 域名下页面上传
        if (!trackCode || !window || !location || location.hostname.includes('pay')) {
            return;
        }

        const { itemParams = {} } = options;

        // 构建埋点数据并添加到队列
        const item = this.buildExposureItem(trackCode, itemParams);
        if (!item) return;

        this.queue.push(item);

        if (this.requestInstance) {
            this.sendExposureQueue(this.requestInstance);
        }
    };
}


export const exposure = new SLExposure({
    listenRequestInstance: true
});
